import { Component } from "react";
import PropTypes from "prop-types";
import { Card, Row, Col, Form, Button, Table, Spinner, Alert, Badge } from "react-bootstrap";
import { FaFilePdf, FaCheck } from "react-icons/fa";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import api from "../../services/api";
import { withRouter } from "../../common/with-router";
import { formataDataHoraExibicao, formataTelefoneExibicao } from "../../common/utils";

const MySwal = withReactContent(Swal);

/**
 * Assinatura da declaração de indicação pelo médico, por link aberto — sem login.
 *
 * A navegadora preenche os dados do profissional indicado e envia este link para o médico
 * apenas assinar. É o caminho principal, e não a exceção: a maioria dos médicos da Etapa 1
 * não tem acesso à plataforma.
 *
 * Reaberto depois de assinada, o link mostra o documento assinado em vez do formulário —
 * ninguém assina duas vezes, e quem guardou o e-mail continua conseguindo ver o que assinou.
 */
class AssinaturaIndicacao extends Component {
    constructor(props) {
        super(props);
        this.state = {
            carregando: true,
            erro: "",
            dados: null,
            autoriza_contato: null,
            assino: false,
            erros: {},
            salvando: false,
        };
    }

    componentDidMount() {
        this.carregar();
    }

    uuid = () => this.props.router?.params?.uuid || "";

    carregar = async () => {
        try {
            const { data } = await api.get(`/pessoas/indicacoes-profissionais/assinatura/${this.uuid()}/`, {
                headers: { "X-USERNAME": import.meta.env.VITE_X_USERNAME },
            });
            this.setState({ carregando: false, dados: data, autoriza_contato: data.autoriza_contato });
        } catch (error) {
            const detalhe = error.response?.data?.detail;
            this.setState({
                carregando: false,
                erro: detalhe || "Não foi possível abrir esta declaração. Confira o link recebido.",
            });
        }
    };

    assinar = async (e) => {
        e.preventDefault();
        const erros = {};
        if (this.state.autoriza_contato === null) erros.autoriza_contato = "Responda a autorização de contato.";
        if (!this.state.assino) erros.assino = "Confirme a leitura e a assinatura da declaração.";
        this.setState({ erros });
        if (Object.keys(erros).length) return;

        this.setState({ salvando: true });
        try {
            await api.post(
                `/pessoas/indicacoes-profissionais/assinatura/${this.uuid()}/`,
                { autoriza_contato: this.state.autoriza_contato },
                { headers: { "X-USERNAME": import.meta.env.VITE_X_USERNAME } }
            );
            await MySwal.fire({
                icon: "success",
                title: "Declaração assinada",
                text: "Obrigado. O programa foi avisado e dará andamento à indicação.",
                confirmButtonText: "Entendi",
            });
            this.setState({ salvando: false });
            await this.carregar();
        } catch (error) {
            console.error("Erro ao assinar a declaração:", error);
            this.setState({ salvando: false });
            const dados = error.response?.data || {};
            MySwal.fire({
                icon: "error", title: "Atenção!", showCloseButton: true,
                text: dados.detail || Object.values(dados).flat()[0] ||
                      "Não foi possível registrar a assinatura. Tente novamente.",
            });
        }
    };

    renderDados = () => {
        const { dados } = this.state;
        const conselho = [dados.indicado.conselho, dados.indicado.registro, dados.indicado.registro_uf]
            .filter(Boolean).join(" ") || "-";
        const crm = [dados.medico.registro, dados.medico.registro_uf].filter(Boolean).join("-") || "-";

        return (
            <Table responsive className="custom-table mb-4">
                <tbody>
                    <tr><th style={{ width: "38%" }}>Médico</th><td>{dados.medico.nome || "-"} — CRM {crm}</td></tr>
                    <tr><th>Profissional indicado</th><td>{dados.indicado.nome || "-"}</td></tr>
                    <tr><th>Conselho</th><td>{conselho}</td></tr>
                    <tr><th>Telefone</th><td>{formataTelefoneExibicao(dados.indicado.telefone) || "-"}</td></tr>
                    <tr><th>E-mail</th><td>{dados.indicado.email || "-"}</td></tr>
                    <tr><th>Programa</th><td>{dados.programa.nome || "-"}</td></tr>
                </tbody>
            </Table>
        );
    };

    /** Já assinada: mostra o documento, não o formulário. */
    renderAssinada = () => {
        const { dados } = this.state;
        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                        <h5 className="mb-0">Declaração de indicação</h5>
                        <Badge bg="success">
                            Assinada em {dados.data_assinatura ? formataDataHoraExibicao(dados.data_assinatura) : "-"}
                        </Badge>
                    </div>
                    {this.renderDados()}
                    <p className="text-muted">
                        Esta declaração já foi assinada. Nada mais é necessário da sua parte.
                    </p>
                    {dados.comprovante_url && (
                        <Button variant="success" href={dados.comprovante_url} target="_blank" rel="noopener noreferrer">
                            <FaFilePdf className="me-1" /> Ver declaração assinada
                        </Button>
                    )}
                </Card.Body>
            </Card>
        );
    };

    renderFormulario = () => {
        const { dados, autoriza_contato, assino, erros, salvando } = this.state;

        return (
            <Card className="cardDestaque">
                <Card.Body>
                    <h5>Declaração de indicação de profissional de saúde</h5>
                    <p className="text-muted">
                        Os dados abaixo foram informados pelo programa. Confira, responda a autorização
                        de contato e assine.
                    </p>

                    {this.renderDados()}

                    <Form onSubmit={this.assinar}>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                Autorizo que o Programa de Suporte ao Paciente realize contato diretamente comigo
                                e/ou com o(a) profissional por mim indicado(a), sempre que necessário, para
                                esclarecimento de dúvidas e/ou comunicação de informações urgentes ou emergenciais.*
                            </Form.Label>
                            <div>
                                {[["SIM", true], ["NÃO", false]].map(([rotulo, valor]) => (
                                    <Form.Check inline type="radio" key={rotulo} id={`autoriza_${rotulo}`}
                                                name="autoriza_contato" label={rotulo}
                                                checked={autoriza_contato === valor}
                                                onChange={() => this.setState({
                                                    autoriza_contato: valor,
                                                    erros: { ...erros, autoriza_contato: "" },
                                                })} />
                                ))}
                            </div>
                            {erros.autoriza_contato && (
                                <div className="text-danger small mt-1">{erros.autoriza_contato}</div>
                            )}
                        </Form.Group>

                        <Alert variant="light" className="border">
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                                <div>
                                    <strong>Declaração</strong>
                                    <div className="text-muted small">
                                        Ao confirmar, você assina eletronicamente a declaração com os dados acima.
                                    </div>
                                </div>
                                {dados.declaracao_url && (
                                    <Button variant="outline-secondary" size="sm" href={dados.declaracao_url}
                                            target="_blank" rel="noopener noreferrer">
                                        <FaFilePdf className="me-1" /> Ler a declaração
                                    </Button>
                                )}
                            </div>

                            <Form.Check
                                type="checkbox" id="assino_declaracao" className="mt-3"
                                label="Li a declaração e assino eletronicamente as informações acima."
                                checked={assino}
                                onChange={() => this.setState({ assino: !assino, erros: { ...erros, assino: "" } })}
                            />
                            {erros.assino && <div className="text-danger small mt-1">{erros.assino}</div>}
                        </Alert>

                        <Button variant="success" type="submit" className="btn-submit" disabled={salvando}>
                            {salvando ? <span className="spinner-border spinner-border-sm"></span>
                                      : <><FaCheck className="me-1" /> Assinar declaração</>}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        );
    };

    render() {
        const { carregando, erro, dados } = this.state;

        return (
            <Row className="justify-content-center">
                <Col lg={9} xl={8}>
                    {/* a logo identifica o programa, como nas telas de termo: o médico abre
                        este link direto do e-mail, sem o resto do sistema em volta */}
                    {dados?.programa?.imagem_branca && (
                        <div className="mb-3 mt-2 text-center">
                            <img src={dados.programa.imagem_branca} alt={dados.programa.nome}
                                 style={{ maxWidth: "200px" }} />
                        </div>
                    )}
                    {carregando && (
                        <div className="text-center py-5"><Spinner animation="border" /></div>
                    )}
                    {!carregando && erro && <Alert variant="danger">{erro}</Alert>}
                    {!carregando && !erro && dados && dados.cancelada && (
                        <Alert variant="warning">
                            Esta indicação foi encerrada pelo programa e não precisa mais ser assinada.
                        </Alert>
                    )}
                    {!carregando && !erro && dados && !dados.cancelada && (
                        dados.assinada ? this.renderAssinada() : this.renderFormulario()
                    )}
                </Col>
            </Row>
        );
    }
}

AssinaturaIndicacao.propTypes = {
    router: PropTypes.object,
};

export default withRouter(AssinaturaIndicacao);
